import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './PrimeTimes.css';

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const getServerNumber = (server) => {
  if (!server) return 9999;
  const match = String(server).match(/RU(\d+)/i);
  return match ? parseInt(match[1], 10) : 9999;
};

const PrimeTimes = () => {
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const moderatorsRef = useRef(moderators);
  const pendingSaveRef = useRef(new Map());

  useEffect(() => {
    moderatorsRef.current = moderators;
  }, [moderators]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch('/api/prime-times', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setModerators(data.moderators || []);
      }
    } catch (e) {
      console.error('Prime times load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sortedModerators = useMemo(() => {
    return [...moderators].sort((a, b) => {
      if (a.server !== b.server) {
        const aNum = getServerNumber(a.server);
        const bNum = getServerNumber(b.server);
        if (aNum !== 9999 && bNum !== 9999) return aNum - bNum;
        if (aNum !== 9999 && bNum === 9999) return -1;
        if (aNum === 9999 && bNum !== 9999) return 1;
        return (a.server || '').localeCompare(b.server || '');
      }
      return (a.nickname || '').localeCompare(b.nickname || '');
    });
  }, [moderators]);

  const countByHourWeekday = useCallback(
    (hour) => moderators.filter((m) => (m.hours || []).includes(hour)).length,
    [moderators]
  );
  const countByHourWeekend = useCallback(
    (hour) => moderators.filter((m) => (m.hours_weekend || []).includes(hour)).length,
    [moderators]
  );

  const persistHours = useCallback(async (discordId, hours, hoursWeekend, rollback) => {
    setSavingId(discordId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/prime-times', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          moderator_discord_id: discordId,
          hours,
          hours_weekend: hoursWeekend
        })
      });
      if (!res.ok) throw new Error('save failed');
    } catch (e) {
      console.error('Prime times save error', e);
      if (rollback) {
        setModerators((prev) =>
          prev.map((m) =>
            m.discord_id === discordId
              ? { ...m, hours: rollback.hours, hours_weekend: rollback.hours_weekend }
              : m
          )
        );
      }
    } finally {
      setSavingId((id) => (id === discordId ? null : id));
    }
  }, []);

  const toggleHour = useCallback((discordId, hour, isWeekend) => {
    const currentList = moderatorsRef.current;
    const mod = currentList.find((m) => m.discord_id === discordId);
    if (!mod) return;

    const prevPending = pendingSaveRef.current.get(discordId);
    const rollback = prevPending?.rollback || {
      hours: [...(mod.hours || [])],
      hours_weekend: [...(mod.hours_weekend || [])]
    };

    const current = isWeekend ? (mod.hours_weekend || []) : (mod.hours || []);
    const next = current.includes(hour)
      ? current.filter((h) => h !== hour)
      : [...current, hour].sort((a, b) => a - b);

    const nextWeekday = isWeekend ? [...(mod.hours || [])] : next;
    const nextWeekend = isWeekend ? next : [...(mod.hours_weekend || [])];

    const nextMod = { ...mod, hours: nextWeekday, hours_weekend: nextWeekend };
    moderatorsRef.current = currentList.map((m) =>
      m.discord_id === discordId ? nextMod : m
    );
    setModerators(moderatorsRef.current);

    if (prevPending?.timer) window.clearTimeout(prevPending.timer);

    const timer = window.setTimeout(() => {
      const pending = pendingSaveRef.current.get(discordId);
      pendingSaveRef.current.delete(discordId);
      if (!pending) return;
      persistHours(discordId, pending.hours, pending.hoursWeekend, pending.rollback);
    }, 200);

    pendingSaveRef.current.set(discordId, {
      timer,
      hours: nextWeekday,
      hoursWeekend: nextWeekend,
      rollback
    });
  }, [persistHours]);

  if (loading) {
    return (
      <div className="prime-times-container">
        <div className="prime-times-loading"><span>Загрузка...</span></div>
      </div>
    );
  }

  return (
    <div className="prime-times-container">
      <div className="prime-times-toolbar">
        <div className="prime-times-legend">
          <span className="prime-times-legend-item">
            <span className="prime-times-legend-swatch weekday" />
            Будни (Б)
          </span>
          <span className="prime-times-legend-item">
            <span className="prime-times-legend-swatch weekend" />
            Выходные (В)
          </span>
        </div>
        <div className="prime-times-hint">Нажмите на ячейку, чтобы включить или выключить час</div>
      </div>

      <div className="prime-times-table-wrap">
        <table className="prime-times-table">
          <thead>
            <tr>
              <th className="prime-times-cell-moderator">Менеджер</th>
              <th className="prime-times-cell-bv" title="Будни / Выходные"> </th>
              {HOURS.map((h) => (
                <th
                  key={h}
                  className="prime-times-cell-hour"
                  title={`${String(h).padStart(2, '0')}:00`}
                >
                  <span className="prime-times-hour-label">{String(h).padStart(2, '0')}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedModerators.length === 0 ? (
              <tr>
                <td colSpan={26} className="prime-times-empty">
                  Нет менеджеров для отображения (все — Junior или руководство).
                </td>
              </tr>
            ) : (
              sortedModerators.map((mod, index) => {
                const isSaving = savingId === mod.discord_id;
                return (
                  <React.Fragment key={mod.discord_id}>
                    <tr className="prime-times-row-b prime-times-moderator-row">
                      <td className="prime-times-cell-moderator" rowSpan={2}>
                        <div className="prime-times-moderator-content">
                          <span className="prime-times-nickname">{mod.nickname}</span>
                          {isSaving && <span className="prime-times-saving-badge">сохранение...</span>}
                        </div>
                      </td>
                      <td className="prime-times-cell-bv prime-times-bv-b" title="Будни">Б</td>
                      {HOURS.map((hour) => {
                        const active = (mod.hours || []).includes(hour);
                        return (
                          <td key={hour} className="prime-times-cell-hour">
                            <button
                              type="button"
                              className={`prime-times-hour-btn ${active ? 'active' : ''}`}
                              onClick={() => toggleHour(mod.discord_id, hour, false)}
                              onPointerDown={(e) => e.stopPropagation()}
                              title={`${mod.nickname} · будни · ${String(hour).padStart(2, '0')}:00`}
                              aria-label={`${mod.nickname}, будни, ${String(hour).padStart(2, '0')}:00`}
                              aria-pressed={active}
                            />
                          </td>
                        );
                      })}
                    </tr>
                    <tr
                      className={`prime-times-row-v prime-times-moderator-row ${
                        index < sortedModerators.length - 1 ? 'prime-times-row-separated' : ''
                      }`}
                    >
                      <td className="prime-times-cell-bv prime-times-bv-v" title="Выходные">В</td>
                      {HOURS.map((hour) => {
                        const active = (mod.hours_weekend || []).includes(hour);
                        return (
                          <td key={hour} className="prime-times-cell-hour">
                            <button
                              type="button"
                              className={`prime-times-hour-btn ${active ? 'active-weekend' : ''}`}
                              onClick={() => toggleHour(mod.discord_id, hour, true)}
                              onPointerDown={(e) => e.stopPropagation()}
                              title={`${mod.nickname} · выходные · ${String(hour).padStart(2, '0')}:00`}
                              aria-label={`${mod.nickname}, выходные, ${String(hour).padStart(2, '0')}:00`}
                              aria-pressed={active}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                );
              })
            )}
            {sortedModerators.length > 0 && (
              <tr className="prime-times-total-row">
                <td className="prime-times-cell-moderator prime-times-total-label">Итого</td>
                <td className="prime-times-cell-bv prime-times-total-bv">Б/В</td>
                {HOURS.map((hour) => (
                  <td key={hour} className="prime-times-cell-hour prime-times-total-cell">
                    <span className="prime-times-total-part prime-times-total-b">{countByHourWeekday(hour) || '0'}</span>
                    <span className="prime-times-total-sep">/</span>
                    <span className="prime-times-total-part prime-times-total-v">{countByHourWeekend(hour) || '0'}</span>
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PrimeTimes;
